import React, { useState } from 'react';
import { Check, Loader2, Mail } from 'lucide-react';

import { useI18n } from '@/i18n/LanguageContext';
import { crmSubscribe } from '@/contexts/AuthContext';
import { submitForm, AUDIENCE_META, Audience } from '@/lib/crmForms';
import { TRADES, tradeLabel } from '@/data/constants';

/**
 * THE reusable visitor / newsletter lead-capture form.
 *
 * One component, used everywhere we want an email (home, jobs, trade pages,
 * pricing, resources, footer-style blocks). Every submission goes to BOTH:
 *
 *   1. submitForm() -> crm-capture -> GoHighLevel contact upsert with the
 *      audience tag (Homeowner / Contractor / Subcontractor / Visitor), the
 *      trade tag, the SMS consent flag and full UTM attribution. This is what
 *      starts the onboarding / nurture workflow in GHL.
 *   2. crmSubscribe() -> the platform's own subscribe endpoint, which keeps
 *      the marketing list and the job-alert digest in sync.
 *
 * Both calls are non-blocking for the visitor: if the CRM is down the payload
 * is queued server-side and retried, and the visitor still sees success.
 */

export interface LeadCaptureFormProps {
  /** Which nurture sequence this signup belongs to. */
  audience?: Audience;
  /** Where on the site the form lives — becomes the CRM `source`. */
  source: string;
  title?: string;
  subtitle?: string;
  /** Ask which trade they work in (contractor / subcontractor capture). */
  askTrade?: boolean;
  /** Pre-set the trade (trade landing pages). */
  trade?: string;
  /** Pre-set city/state (city + county landing pages). */
  city?: string;
  state?: string;
  tags?: string[];
  variant?: 'card' | 'inline' | 'dark';
  className?: string;
}

const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({
  audience = 'visitor',
  source,
  title,
  subtitle,
  askTrade = false,
  trade,
  city,
  state,
  tags = [],
  variant = 'card',
  className = '',
}) => {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pickedTrade, setPickedTrade] = useState(trade || '');
  const [sms, setSms] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const meta = AUDIENCE_META[audience];
  const dark = variant === 'dark';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setErr(t('auth.errEmail'));
      return;
    }
    setErr('');
    setBusy(true);

    const chosenTrade = pickedTrade || trade || '';
    const allTags = [...meta.tags, ...tags, `source:${source}`, `nurture:${meta.nurture}`];

    // 1) GoHighLevel (tags + custom fields + workflow trigger)
    await submitForm({
      formType: 'newsletter',
      email: email.trim().toLowerCase(),
      name: name.trim() || undefined,
      phone: phone.trim() || undefined,
      language: lang,
      userType: meta.userType,
      smsOptIn: sms === true,
      consent: true,
      city,
      state,
      trade: chosenTrade || undefined,
      tradeSlug: chosenTrade || undefined,
      tradeLabel: chosenTrade ? tradeLabel(chosenTrade, lang) : undefined,
      tags: allTags,
      fields: { signup_source: source, audience },
    });

    // 2) Platform subscribe list (job-alert digest + marketing list)
    await crmSubscribe({
      email: email.trim().toLowerCase(),
      name: name.trim() || undefined,
      phone: phone.trim() || undefined,
      sms_opt_in: sms === true,
      source,
      tags: ['newsletter', `audience:${audience}`, ...(chosenTrade ? [`trade:${chosenTrade}`] : []), `lang:${lang}`],
      language: lang,
    });

    setBusy(false);
    setDone(true);
    setEmail('');
    setName('');
    setPhone('');
  };

  const inputCls = dark
    ? 'w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none'
    : 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-orange-500 focus:outline-none';

  const wrapCls =
    variant === 'card'
      ? 'rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'
      : variant === 'dark'
      ? 'rounded-2xl border border-white/10 bg-slate-900 p-6'
      : '';

  if (done) {
    return (
      <div className={`${wrapCls} ${className}`}>
        <p
          className={`flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-semibold ${
            dark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          <Check className="h-4 w-4 shrink-0" /> {t('home.newsletterOk')}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`${wrapCls} ${className}`}>
      <p className={`flex items-center gap-2 text-base font-bold ${dark ? 'text-white' : 'text-slate-900'}`}>
        <Mail className={`h-4 w-4 ${dark ? 'text-orange-400' : 'text-orange-500'}`} />
        {title || t('home.newsletterTitle')}
      </p>
      <p className={`mt-1 text-sm ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
        {subtitle || t('home.newsletterSub')}
      </p>

      <div className="mt-4 space-y-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('common.name')}
          autoComplete="name"
          className={inputCls}
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('common.email')}
          autoComplete="email"
          className={inputCls}
        />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t('home.phonePlaceholder')}
          autoComplete="tel"
          className={inputCls}
        />

        {askTrade && (
          <select value={pickedTrade} onChange={(e) => setPickedTrade(e.target.value)} className={inputCls}>
            <option value="">{t('common.trade')}</option>
            {TRADES.map((tr) => (
              <option key={tr.slug} value={tr.slug}>
                {tradeLabel(tr.slug, lang)}
              </option>
            ))}
          </select>
        )}

        <label className={`flex items-start gap-2 text-xs ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          <input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} className="mt-0.5" />
          <span>{t('home.smsOptIn')}</span>
        </label>

        {err && <p className="text-xs font-semibold text-red-500">{err}</p>}

        <button
          type="submit"
          disabled={busy}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('common.submit')}
        </button>
      </div>
    </form>
  );
};

export default LeadCaptureForm;
